export interface FieldAssignment {
    code: string;
    startLine: number;
    endLine: number;
}

const FIELD_CODE_SOURCE = '([A-Za-z0-9._-]+)';
// Use \n? (not \n*) to consume only the single structural newline between
// a marker comment and the field content. Using \n* would greedily consume
// blank lines that are part of the content, stripping them from cleanContent
// and causing line-count mismatches with field assignment positions.
const FIELD_BLOCK_PATTERN_SOURCE = `(?:<!--\\s*field-start:${FIELD_CODE_SOURCE}\\s*-->|\\[field-start:${FIELD_CODE_SOURCE}\\])\\n?([\\s\\S]*?)\\n?(?:<!--\\s*field-end:(?:\\1|\\2)\\s*-->|\\[field-end:(?:\\1|\\2)\\])`;
const FIELD_BLOCK_PATTERN = new RegExp(FIELD_BLOCK_PATTERN_SOURCE, 'g');

const countLines = (text: string) => text.split('\n').length;
const countLineBreaks = (text: string) => (text.match(/\n/g) || []).length;

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const createFieldBlockPattern = (): RegExp => new RegExp(FIELD_BLOCK_PATTERN_SOURCE, 'g');

export const createFieldStartPattern = (): RegExp => new RegExp(
    `(?:<!--\\s*field-start:${FIELD_CODE_SOURCE}\\s*-->|\\[field-start:${FIELD_CODE_SOURCE}\\])`,
    'g'
);

export const createFieldEndPattern = (code: string): RegExp => new RegExp(
    `(?:<!--\\s*field-end:${escapeRegExp(code)}\\s*-->|\\[field-end:${escapeRegExp(code)}\\])`
);

export const buildFieldStartMarker = (code: string): string => `<!-- field-start:${code} -->`;

export const buildFieldEndMarker = (code: string): string => `<!-- field-end:${code} -->`;

export const buildFieldBlock = (code: string, body = ''): string => {
    if (body.trim()) {
        return `${buildFieldStartMarker(code)}\n${body}\n${buildFieldEndMarker(code)}`;
    }

    return `${buildFieldStartMarker(code)}\n\n${buildFieldEndMarker(code)}`;
};

export const hasStructuredFieldMarkers = (markdown: string): boolean => new RegExp(FIELD_BLOCK_PATTERN).test(markdown);

export const normalizeAssignments = (assignments: FieldAssignment[], lineCount: number): FieldAssignment[] => {
    const maxLine = Math.max(lineCount - 1, 0);

    return assignments
        .map((assignment) => ({
            code: assignment.code,
            startLine: Math.max(0, Math.min(assignment.startLine, maxLine)),
            endLine: Math.max(0, Math.min(assignment.endLine, maxLine)),
        }))
        .filter((assignment) => assignment.code && assignment.startLine <= assignment.endLine)
        .sort((left, right) => left.startLine - right.startLine || left.endLine - right.endLine);
};

/**
 * Detects and repairs assignments that were corrupted by a compose/parse
 * round-trip bug where markers ended up wrapping empty lines instead of
 * actual content sections. The corruption pattern is: most assignments
 * span only 1 line (typically an empty line or a heading).
 *
 * Repair strategy: find section headings in the clean content, skip the
 * document title (first H1), and assign each remaining heading+section
 * sequentially to each field.
 */
const repairCorruptedAssignments = (assignments: FieldAssignment[], cleanContent: string): FieldAssignment[] => {
    if (assignments.length < 2) {
        return assignments;
    }

    const lines = cleanContent.split('\n');
    const totalLines = lines.length;

    // Detect corruption: if more than half the assignments span only 1 line
    // and cover empty / heading-only content, they were almost certainly corrupted.
    const singleLineCount = assignments.filter(a => a.startLine === a.endLine).length;
    if (singleLineCount < Math.ceil(assignments.length * 0.5)) {
        return assignments; // Looks healthy — don't touch
    }

    // Check if the single-line assignments actually cover empty or heading lines
    const corruptedCount = assignments.filter(a => {
        if (a.startLine !== a.endLine) return false;
        const lineContent = lines[a.startLine]?.trim() || '';
        return lineContent === '' || lineContent.startsWith('#');
    }).length;

    if (corruptedCount < Math.ceil(assignments.length * 0.5)) {
        return assignments; // Single-line but covering real content — don't repair
    }

    // Find all heading line indices (## and above)
    const headingLines: number[] = [];
    lines.forEach((line, i) => {
        if (line.trimStart().startsWith('#')) {
            headingLines.push(i);
        }
    });

    // Skip the document title (first heading, typically H1) so we only get
    // section headings that correspond to field assignments.
    const sectionHeadings = headingLines.length > assignments.length
        ? headingLines.slice(1)
        : headingLines;

    // If we don't have enough headings to map to fields, can't repair reliably
    if (sectionHeadings.length < assignments.length) {
        return assignments;
    }

    // Assign each field to a sequential heading and its content section.
    const repaired: FieldAssignment[] = assignments.map((assignment, fieldIndex) => {
        const sectionStart = sectionHeadings[fieldIndex];

        // Section ends just before the next heading, or at end of content
        let sectionEnd: number;
        if (fieldIndex < sectionHeadings.length - 1) {
            sectionEnd = sectionHeadings[fieldIndex + 1] - 1;
        } else {
            sectionEnd = totalLines - 1;
        }

        // Trim trailing empty lines from the end of this section
        while (sectionEnd > sectionStart && lines[sectionEnd]?.trim() === '') {
            sectionEnd--;
        }

        return { code: assignment.code, startLine: sectionStart, endLine: sectionEnd };
    });

    return normalizeAssignments(repaired, totalLines);
};

export const parseStructuredFieldMarkdown = (markdown: string): { cleanContent: string; assignments: FieldAssignment[] } => {
    if (!markdown) {
        return {
            cleanContent: '',
            assignments: [],
        };
    }

    const fieldPattern = createFieldBlockPattern();
    const pendingAssignments: Array<{ code: string; charStart: number; charEnd: number }> = [];
    const cleanParts: string[] = [];
    let cursor = 0;
    let cleanCharCursor = 0;

    for (let match = fieldPattern.exec(markdown); match; match = fieldPattern.exec(markdown)) {
        const matchStart = match.index;
        const fullMatch = match[0];
        const code = match[1] || match[2] || '';
        const content = match[3] || '';

        if (matchStart > cursor) {
            const plainContent = markdown.slice(cursor, matchStart);
            cleanParts.push(plainContent);
            cleanCharCursor += plainContent.length;
        }

        const contentStartChar = cleanCharCursor;
        cleanParts.push(content);
        cleanCharCursor += content.length;

        pendingAssignments.push({
            code,
            charStart: contentStartChar,
            charEnd: cleanCharCursor,
        });

        cursor = matchStart + fullMatch.length;
    }

    if (cursor < markdown.length) {
        cleanParts.push(markdown.slice(cursor));
    }

    const cleanContent = cleanParts.join('');
    const lineCount = cleanContent.length === 0 ? 1 : countLines(cleanContent);

    // Convert character offsets to line numbers
    const assignments: FieldAssignment[] = pendingAssignments.map((pending) => {
        const startLine = countLineBreaks(cleanContent.slice(0, pending.charStart));
        // For endLine, count up to the last character of the content (charEnd - 1)
        // so the endLine is the line containing the final character, not the line after it
        const endLine = pending.charEnd > pending.charStart
            ? countLineBreaks(cleanContent.slice(0, pending.charEnd - 1))
            : startLine;
        return { code: pending.code, startLine, endLine };
    });

    const normalized = normalizeAssignments(assignments, lineCount);

    // Repair corrupted assignments: if most fields only span 1 line (typically
    // wrapping empty lines due to a previous compose/parse bug), redistribute
    // them to cover the actual content sections between headings.
    const repairedAssignments = repairCorruptedAssignments(normalized, cleanContent);

    // Exclude leading heading and blank lines from field assignments so that
    // section headings are not visually selected as part of the field content.
    // The backend/AI often wraps headings inside markers, but they should be
    // shown above the field selection, not inside it.
    const lines = cleanContent.split('\n');
    const headingAdjusted = repairedAssignments.map((assignment) => {
        let { startLine } = assignment;
        while (
            startLine < assignment.endLine &&
            (lines[startLine]?.trimStart().startsWith('#') || lines[startLine]?.trim() === '')
        ) {
            startLine += 1;
        }
        return startLine !== assignment.startLine
            ? { ...assignment, startLine }
            : assignment;
    });

    return {
        cleanContent,
        assignments: headingAdjusted,
    };
};

export const composeStructuredFieldMarkdown = (cleanContent: string, assignments: FieldAssignment[]): string => {
    if (!cleanContent) {
        return '';
    }

    const lines = cleanContent.split('\n');
    const safeAssignments = normalizeAssignments(assignments, lines.length);
    const startMap = new Map<number, FieldAssignment>();
    const endMap = new Map<number, FieldAssignment>();

    safeAssignments.forEach((assignment) => {
        startMap.set(assignment.startLine, assignment);
        endMap.set(assignment.endLine, assignment);
    });

    const output: string[] = [];

    lines.forEach((line, index) => {
        const startAssignment = startMap.get(index);
        if (startAssignment) {
            output.push(buildFieldStartMarker(startAssignment.code));
        }

        output.push(line);

        const endAssignment = endMap.get(index);
        if (endAssignment) {
            output.push(buildFieldEndMarker(endAssignment.code));
        }
    });

    return output.join('\n');
};
